import React, { useState, useEffect, useRef, useMemo } from "react"
import Tab from "./Tab"
import socket from "socket"

import ArchiveIcon from "components/Common/Icons/HeaderIcons/ArchiveIcon"
import NewTabIcon from "components/Common/Icons/HeaderIcons/NewTabIcon"

import { useSelector } from "react-redux"
import CreateNewChat from "pages/Home/CreateNewChat"
import { deleteClosedTab, updateOpenTabs } from "store/actions"
import { useDispatch } from "react-redux"
import usePreviousRoute from "components/CustomHooks/PreviousRoute"

const getTabKey = tab => tab?._id || tab?.client

const isValidTab = tab => {
  const tabKey = getTabKey(tab)

  return (
    tab &&
    typeof tab === "object" &&
    tabKey &&
    tabKey !== "undefined" &&
    tabKey !== "null"
  )
}

function TabsConstainer() {
  const { rawOpenChatsTabs } = useSelector(state => ({
    rawOpenChatsTabs: state.Login.openChatsTabs,
  }))

  const openChatsTabs = useMemo(
    () =>
      Array.isArray(rawOpenChatsTabs)
        ? rawOpenChatsTabs.filter(isValidTab)
        : [],
    [rawOpenChatsTabs]
  )

  const tabsContainerRef = useRef(null)
  const dispatch = useDispatch()
  const previousRoute = usePreviousRoute()

  const [createChatModal, setCreateChatModal] = useState(false)
  const toggleCreateChatModal = () => setCreateChatModal(!createChatModal)

  const tabs = useMemo(
    () => openChatsTabs.map(chat => getTabKey(chat)),
    [openChatsTabs]
  )

  useEffect(() => {
    localStorage.setItem("openChatsTabs", JSON.stringify(openChatsTabs))
  }, [openChatsTabs])

  useEffect(() => {
    if (!Array.isArray(rawOpenChatsTabs)) {
      dispatch(updateOpenTabs([]))
      return
    }

    if (rawOpenChatsTabs.length !== openChatsTabs.length) {
      dispatch(updateOpenTabs(openChatsTabs))
    }
  }, [rawOpenChatsTabs, openChatsTabs, dispatch])

  useEffect(() => {
    const tabsContainer = tabsContainerRef.current

    const handleWheel = event => {
      event.preventDefault()
      tabsContainer.scrollBy({
        left: event.deltaY,
        behavior: "smooth",
      })
    }

    tabsContainer.addEventListener("wheel", handleWheel)

    return () => {
      tabsContainer.removeEventListener("wheel", handleWheel)
    }
  }, [])

  const previousTabsRef = useRef([])

  useEffect(() => {
    if (socket) {
      const handleUpdating = () => {
        socket.emit("client_to_server", { category: "chats", tabs: tabs })
      }

      const handleServerToClient = response => {
        if (response.tabs) {
          const updatedTabs = response.tabs
            .map(tab => tab.chat)
            .filter(isValidTab)

          // Update state and local storage with the new tabs
          dispatch(updateOpenTabs(updatedTabs))
          localStorage.setItem("openChatsTabs", JSON.stringify(updatedTabs))

          // Compare the current status with the previous status
          updatedTabs.forEach(chat => {
            const previousChat = Array.isArray(previousTabsRef.current)
              ? previousTabsRef.current.filter(
                  c => getTabKey(c) === getTabKey(chat)
                )[0]
              : undefined

            const tabToDelete =
              previousRoute.split("/")[previousRoute.split("/").length - 1]

            if (
              previousChat &&
              previousChat.status === "open" &&
              chat.status === "archived" &&
              previousRoute.includes(getTabKey(chat))
            ) {
              dispatch(deleteClosedTab(tabToDelete))
            }
          })

          previousTabsRef.current = updatedTabs
        }
      }

      socket.on("updating", handleUpdating)
      socket.on("server_to_client", handleServerToClient)

      return () => {
        socket.off("updating", handleUpdating)
        socket.off("server_to_client", handleServerToClient)
      }
    }
  }, [
    tabs,
    openChatsTabs.length,
    previousRoute,
    previousTabsRef.current,
    socket,
  ])

  return (
    <div className="tabs" ref={tabsContainerRef}>
      {openChatsTabs.map((tab, idx) => (
        <Tab
          key={getTabKey(tab)}
          name={
            tab.endUser
              ? tab.endUser.name
              : tab.contactName?.name
              ? tab.contactName?.name
              : tab.client
          }
          index={idx}
          openChatsTabs={openChatsTabs}
          tab={tab}
        >
          {tab.lastSession?.status === "onTime" ? (
            <i
              className="bx bxs-time"
              style={{ color: "#2AAA4A", fontSize: "16px" }}
            ></i>
          ) : tab.status === "archived" ? (
            <ArchiveIcon />
          ) : tab.lastSession?.status === "danger" ? (
            <i
              className="bx bxs-time"
              style={{ color: "#F3A82D", fontSize: "16px" }}
            ></i>
          ) : tab.lastSession?.status === "tooLate" ? (
            <i
              className="bx bxs-time"
              style={{ color: "#DF4128", fontSize: "16px" }}
            ></i>
          ) : tab.lastSession?.status === "open" ? (
            <i
              className="bx bxs-time"
              style={{ color: "#3879f0", fontSize: "16px" }}
            ></i>
          ) : (
            <i
              className="bx bxs-time"
              style={{ color: "transparent", fontSize: "16px" }}
            >
              {/* {console.log('tab', tab)} */}
            </i>
          )}
        </Tab>
      ))}

      {openChatsTabs.length > 0 && (
        <div className="add-new-chat" onClick={toggleCreateChatModal}>
          <NewTabIcon />
        </div>
      )}

      <CreateNewChat
        createChatModal={createChatModal}
        toggleCreateChatModal={toggleCreateChatModal}
      />
    </div>
  )
}

export default TabsConstainer
